import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { createNoise3D } from 'simplex-noise';
import './CanvasComponent.css';

const CanvasComponent = () => {
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    let renderer, scene, camera, sphereBg, nucleus, stars, asteroid1, asteroid2, controls;
    const noise = createNoise3D();
    const blobScale = 3;

    const container = containerRef.current;

    const init = () => {
      // Scene and Camera
      scene = new THREE.Scene();
      camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.01, 1000);
      camera.position.set(0, 0, 230);
      camera.lookAt(scene.position);

      // Lighting
      const directionalLight = new THREE.DirectionalLight('#fff', 2);
      directionalLight.position.set(0, 50, -20);
      scene.add(directionalLight);

      const ambientLight = new THREE.AmbientLight('#ffffff', 1);
      scene.add(ambientLight);

      // Renderer
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.setPixelRatio(window.devicePixelRatio);
      container.appendChild(renderer.domElement);

      // Orbit Controls
      controls = new OrbitControls(camera, renderer.domElement);
      controls.autoRotate = true;
      controls.autoRotateSpeed = 4;
      controls.maxDistance = 350;
      controls.minDistance = 150;
      controls.enablePan = false;

      // Textures
      const loader = new THREE.TextureLoader();

      // Load the sphere background texture
      const textureSphereBg = loader.load(
        'https://user-images.githubusercontent.com/26748614/96337246-f14d4580-1085-11eb-8793-a86d929e034d.jpg'
      );

      // Load nucleus texture
      const texturenucleus = loader.load('https://i.ibb.co/4gHcRZD/bg3-je3ddz.jpg');

      // Load asteroid texture
      const asteroidTexture = loader.load('https://i.ibb.co/26tww9y/earth.jpg'); // Realistic asteroid texture

      // Nucleus (Earth)
      texturenucleus.anisotropy = 16;
      const icosahedronGeometry = new THREE.IcosahedronGeometry(60, 10); // Increase size for visibility
      const lambertMaterial = new THREE.MeshBasicMaterial({
        map: texturenucleus,
      });
      nucleus = new THREE.Mesh(icosahedronGeometry, lambertMaterial);
      scene.add(nucleus);

      // Sphere Background
      textureSphereBg.anisotropy = 16;
      const geometrySphereBg = new THREE.SphereGeometry(120, 50, 100);
      const materialSphereBg = new THREE.MeshBasicMaterial({
        side: THREE.BackSide,
        transparent: true,
        map: textureSphereBg,
      });
      sphereBg = new THREE.Mesh(geometrySphereBg, materialSphereBg);
      scene.add(sphereBg);

      // Asteroids
      const asteroidGeometry = new THREE.IcosahedronGeometry(10, 4); // Smaller size for asteroids
      const asteroidMaterial = new THREE.MeshBasicMaterial({
        map: asteroidTexture, // Realistic texture for the asteroids
      });

      asteroid1 = new THREE.Mesh(asteroidGeometry, asteroidMaterial);
      asteroid2 = new THREE.Mesh(asteroidGeometry, asteroidMaterial);
      scene.add(asteroid1);
      scene.add(asteroid2);

      // Moving Stars
      const starsGeometry = new THREE.BufferGeometry();
      const starVertices = [];
      for (let i = 0; i < 50; i++) {
        const particleStar = randomPointSphere(150);
        starVertices.push(particleStar.x, particleStar.y, particleStar.z);
      }
      starsGeometry.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(starVertices, 3)
      );

      const starsMaterial = new THREE.PointsMaterial({
        size: 5,
        color: '#ffffff',
        transparent: true,
        opacity: 0.8,
        map: loader.load('https://i.ibb.co/yYS2yx5/p3-ttfn70.png'),
        blending: THREE.AdditiveBlending,
      });
      starsMaterial.depthWrite = false;
      stars = new THREE.Points(starsGeometry, starsMaterial);
      scene.add(stars);
    };

    const randomPointSphere = (radius) => {
      const theta = 2 * Math.PI * Math.random();
      const phi = Math.acos(2 * Math.random() - 1);
      const x = radius * Math.sin(phi) * Math.cos(theta);
      const y = radius * Math.sin(phi) * Math.sin(theta);
      const z = radius * Math.cos(phi);
      return new THREE.Vector3(x, y, z);
    };

    const animate = () => {
      requestAnimationFrame(animate);
      if (controls) controls.update();

      // Animate asteroid 1
      if (asteroid1) {
        const time1 = Date.now() * 0.001;
        const radius1 = 100;
        asteroid1.position.x = Math.sin(time1) * radius1;
        asteroid1.position.y = Math.cos(time1) * radius1;
        asteroid1.position.z = Math.sin(time1 * 0.5) * radius1;
        asteroid1.rotation.x += 0.02;
        asteroid1.rotation.y += 0.03;
      }

      // Animate asteroid 2
      if (asteroid2) {
        const time2 = Date.now() * 0.002;
        const radius2 = 120;
        asteroid2.position.x = Math.sin(time2) * radius2;
        asteroid2.position.y = Math.cos(time2) * radius2;
        asteroid2.position.z = Math.cos(time2 * 0.5) * radius2;
        asteroid2.rotation.x += 0.03;
        asteroid2.rotation.y += 0.04;
      }

      renderer.render(scene, camera);
    };

    const handleWindowResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleWindowResize);
    init();
    animate();

    return () => {
      window.removeEventListener('resize', handleWindowResize);
      container.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={containerRef} className="canvas-container"></div>;
};

export default CanvasComponent;
